import {
  AppleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RightOutlined,
  WindowsOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cleanupOfflineAgents,
  claimAgent,
  getAgentHealthOverview,
  getLatestAgentRelease,
  listAgents,
} from '../api';
import { useAuth } from '../auth/AuthContext';
import { formatShanghaiDateTime } from '../utils/datetime';
import { resolveErrorMessage } from '../utils/errorMessage';
import './AgentSetup.css';

const { Text } = Typography;

const LOCAL_BRIDGE_BASE_URL = String(process.env.REACT_APP_PUBLISHY_AGENT_BRIDGE_URL || 'http://127.0.0.1:17861').replace(/\/+$/, '');
const LOCAL_REQUEST_TIMEOUT_MS = 2500;
const AGENT_RECENT_ACTIVE_WINDOW_SEC = Math.max(
  300,
  Number(process.env.REACT_APP_AGENT_RECENT_ACTIVE_WINDOW_SEC || 24 * 60 * 60),
);
const AGENT_HISTORY_CLEANUP_WINDOW_SEC = Math.max(
  3600,
  Number(process.env.REACT_APP_AGENT_HISTORY_CLEANUP_WINDOW_SEC || 24 * 60 * 60),
);
const AUTO_REGISTER_ENABLED = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_AGENT_SETUP_AUTO_REGISTER || '').trim().toLowerCase(),
);

const formatDateTime = (value) => formatShanghaiDateTime(value);
const isOnlineAgent = (agent) => agent?.is_online === true;
const parseIsoTimestampMs = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
};
const isRecentActiveAgent = (agent, windowSec = AGENT_RECENT_ACTIVE_WINDOW_SEC) => {
  if (isOnlineAgent(agent)) return true;
  const lastSeenMs = parseIsoTimestampMs(agent?.last_heartbeat_at || agent?.last_seen_at);
  if (!lastSeenMs) return false;
  return lastSeenMs >= (Date.now() - (Math.max(300, Number(windowSec || 0)) * 1000));
};

const withTimeout = async (url, options = {}, timeoutMs = LOCAL_REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      mode: 'cors',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
      ? await response.json()
      : { status: response.ok ? 'success' : 'error', error_message: await response.text() };

    if (!response.ok) {
      throw new Error(resolveErrorMessage(payload?.error_message, `本地 Agent 请求失败（HTTP ${response.status}）`));
    }
    return payload;
  } finally {
    window.clearTimeout(timer);
  }
};

const AgentSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const autoRegisterAttemptRef = useRef('');

  const [activeTab, setActiveTab] = useState('install');

  const [release, setRelease] = useState(null);

  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentKeyword, setAgentKeyword] = useState('');
  const [agents, setAgents] = useState([]);
  const [onlineOnlyAgents, setOnlineOnlyAgents] = useState(false);
  const [showHistoryOfflineAgents, setShowHistoryOfflineAgents] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const [healthOverview, setHealthOverview] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [localState, setLocalState] = useState({
    loading: false,
    reachable: false,
    info: null,
    error: '',
  });

  const [pairFlow, setPairFlow] = useState({
    loading: false,
    state: 'idle',
    lastError: '',
    registeredAgentId: '',
  });
  const [claimAgentId, setClaimAgentId] = useState('');
  const [claimCode, setClaimCode] = useState('');
  const [claimingAgent, setClaimingAgent] = useState(false);

  const [manifestDownloads, setManifestDownloads] = useState({
    windows: '',
    mac: '',
  });

  const envWindowsDownloadUrl = String(process.env.REACT_APP_AGENT_WINDOWS_DOWNLOAD_URL || '').trim();
  const envMacDownloadUrl = String(process.env.REACT_APP_AGENT_MAC_DOWNLOAD_URL || '').trim();
  const manifestUrl = String(process.env.REACT_APP_AGENT_RELEASE_MANIFEST_URL || '/artifacts/agent/release-manifest.json').trim();

  const downloadItems = useMemo(() => {
    const windowsUrl = envWindowsDownloadUrl || String(manifestDownloads.windows || '').trim();
    const macUrl = envMacDownloadUrl || String(manifestDownloads.mac || '').trim() || String(release?.download_url || '').trim();

    return [
      {
        key: 'windows',
        title: 'Windows Agent',
        icon: <WindowsOutlined />,
        desc: '用于运营同学在 Windows 本机执行立即发布和失败接管重跑。',
        url: windowsUrl,
      },
      {
        key: 'mac',
        title: 'Mac Agent',
        icon: <AppleOutlined />,
        desc: '用于运营同学在 macOS 本机执行立即发布和失败接管重跑。',
        url: macUrl,
      },
    ];
  }, [envMacDownloadUrl, envWindowsDownloadUrl, manifestDownloads.mac, manifestDownloads.windows, release?.download_url]);

  const loadRelease = useCallback(async () => {
    try {
      const response = await getLatestAgentRelease();
      if (response?.status !== 'success') {
        throw new Error(resolveErrorMessage(response?.error_message, '获取 Agent 发布信息失败'));
      }
      setRelease(response.release || null);
    } catch (error) {
      message.error(error?.message || '获取 Agent 发布信息失败');
    }
  }, []);

  const loadAgents = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setAgentsLoading(true);
    try {
      const response = await listAgents({
        page: 1,
        page_size: 100,
        keyword: String(agentKeyword || '').trim(),
        online_window_sec: 120,
        include_accounts: true,
      });
      if (response?.status !== 'success') {
        throw new Error(resolveErrorMessage(response?.error_message, '加载 Agent 列表失败'));
      }
      setAgents(Array.isArray(response.items) ? response.items : []);
    } catch (error) {
      if (!silent) {
        message.error(error?.message || '加载 Agent 列表失败');
      }
    } finally {
      if (!silent) setAgentsLoading(false);
    }
  }, [agentKeyword]);

  const detectLocalAgent = useCallback(async ({ silent = false } = {}) => {
    setLocalState((prev) => ({ ...prev, loading: !silent }));
    try {
      const infoResponse = await withTimeout(`${LOCAL_BRIDGE_BASE_URL}/info`, { method: 'GET', headers: {} });
      setLocalState({
        loading: false,
        reachable: infoResponse?.status === 'success',
        info: infoResponse?.agent || null,
        error: '',
      });
    } catch (error) {
      setLocalState({
        loading: false,
        reachable: false,
        info: null,
        error: error?.name === 'AbortError' ? '本地 Agent 检测超时' : (error?.message || '未检测到本地 Agent'),
      });
    }
  }, []);

  const loadHealthOverview = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setHealthLoading(true);
    try {
      const response = await getAgentHealthOverview({ days: 7, top_n: 5, agent_sample_limit: 8, account_sample_limit: 12 });
      if (response?.status !== 'success') {
        throw new Error(resolveErrorMessage(response?.error_message, '加载统一诊断视图失败'));
      }
      setHealthOverview(response);
    } catch (error) {
      if (!silent) {
        message.error(error?.message || '加载统一诊断视图失败');
      }
    } finally {
      if (!silent) setHealthLoading(false);
    }
  }, []);

  const registerLocalAgent = useCallback(async ({ silent = false } = {}) => {
    if (!localState.reachable || !localState.info?.device_id) return false;
    const userId = String(user?.user_id || '').trim();
    if (!userId) return false;

    setPairFlow((prev) => ({
      ...prev,
      loading: true,
      state: 'registering',
      lastError: '',
      registeredAgentId: '',
    }));

    try {
      const localResponse = await withTimeout(`${LOCAL_BRIDGE_BASE_URL}/register`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });
      if (localResponse?.status !== 'success') {
        throw new Error(resolveErrorMessage(localResponse?.error_message, '本地 Agent 注册失败'));
      }

      setPairFlow((prev) => ({
        ...prev,
        loading: false,
        state: 'registered',
        registeredAgentId: String(localResponse?.agent_id || '').trim(),
        lastError: '',
      }));
      if (!silent) message.success('本机 Agent 已注册到当前账号');
      await Promise.all([
        detectLocalAgent({ silent: true }),
        loadAgents({ silent: true }),
        loadHealthOverview({ silent: true }),
      ]);
      return true;
    } catch (error) {
      const errorMessage = error?.message || '注册失败';
      setPairFlow((prev) => ({
        ...prev,
        loading: false,
        state: 'failed',
        lastError: errorMessage,
      }));
      if (!silent) message.error(errorMessage);
      return false;
    }
  }, [detectLocalAgent, loadAgents, loadHealthOverview, localState.info?.device_id, localState.reachable, user?.user_id]);

  useEffect(() => {
    loadRelease();
    loadAgents();
    detectLocalAgent();
    loadHealthOverview();
  }, [detectLocalAgent, loadAgents, loadHealthOverview, loadRelease]);

  useEffect(() => {
    const currentAgentId = String(localState.info?.agent_id || '').trim();
    if (currentAgentId && !String(claimAgentId || '').trim()) {
      setClaimAgentId(currentAgentId);
    }
  }, [claimAgentId, localState.info?.agent_id]);

  useEffect(() => {
    if (!AUTO_REGISTER_ENABLED) return;
    const userId = String(user?.user_id || '').trim();
    const localInfo = localState.info || {};
    const deviceId = String(localInfo?.device_id || '').trim();
    const registeredUserId = String(localInfo?.user_id || '').trim();
    const isRegistered = Boolean(localInfo?.paired);
    const currentAgentId = String(localInfo?.agent_id || '').trim();

    if (!localState.reachable || !userId || !deviceId) return;
    if (isRegistered && registeredUserId === userId) {
      setPairFlow((prev) => ({
        ...prev,
        loading: false,
        state: 'registered',
        registeredAgentId: currentAgentId || prev.registeredAgentId,
        lastError: '',
      }));
      return;
    }
    if (pairFlow.loading) return;

    const attemptKey = `${userId}:${deviceId}:${registeredUserId}:${currentAgentId}:${isRegistered ? 1 : 0}`;
    if (autoRegisterAttemptRef.current === attemptKey) return;
    autoRegisterAttemptRef.current = attemptKey;
    registerLocalAgent({ silent: true });
  }, [
    autoRegisterAttemptRef,
    localState.info,
    localState.reachable,
    pairFlow.loading,
    registerLocalAgent,
    user?.user_id,
  ]);

  useEffect(() => {
    let cancelled = false;
    const shouldLoadManifest = Boolean(manifestUrl) && (!envWindowsDownloadUrl || !envMacDownloadUrl);
    if (!shouldLoadManifest) return undefined;

    const loadManifest = async () => {
      try {
        const response = await fetch(manifestUrl, { cache: 'no-store' });
        if (!response.ok) return;
        const payload = await response.json();
        if (cancelled || !payload || typeof payload !== 'object') return;
        setManifestDownloads({
          windows: String(payload?.windows?.url || '').trim(),
          mac: String(payload?.mac?.url || '').trim(),
        });
      } catch {
        // optional manifest
      }
    };

    loadManifest();
    return () => {
      cancelled = true;
    };
  }, [envMacDownloadUrl, envWindowsDownloadUrl, manifestUrl]);

  const refreshAll = useCallback(() => {
    autoRegisterAttemptRef.current = '';
    loadRelease();
    loadAgents();
    detectLocalAgent();
    loadHealthOverview();
  }, [autoRegisterAttemptRef, detectLocalAgent, loadAgents, loadHealthOverview, loadRelease]);

  const submitClaimAgent = useCallback(async ({ forceReclaim = false } = {}) => {
    const agentId = String(claimAgentId || localState.info?.agent_id || '').trim();
    const code = String(claimCode || '').trim();
    if (!agentId) {
      message.warning('请先输入认领ID');
      return false;
    }
    if (!code) {
      message.warning('请先输入认领码');
      return false;
    }
    try {
      setClaimingAgent(true);
      const payload = {
        agent_id: agentId,
        claim_code: code,
      };
      if (forceReclaim) payload.force_reclaim = true;
      const response = await claimAgent(payload);
      if (response?.status !== 'success') {
        throw new Error(resolveErrorMessage(response?.error_message, '认领失败'));
      }
      setPairFlow((prev) => ({
        ...prev,
        state: 'registered',
        registeredAgentId: agentId,
        lastError: '',
      }));
      setClaimCode('');
      message.success(forceReclaim ? '强制认领成功' : '认领成功');
      await Promise.all([
        detectLocalAgent({ silent: true }),
        loadAgents({ silent: true }),
        loadHealthOverview({ silent: true }),
      ]);
      return true;
    } catch (error) {
      message.error(resolveErrorMessage(error?.message || String(error), '认领失败'));
      return false;
    } finally {
      setClaimingAgent(false);
    }
  }, [claimAgentId, claimCode, detectLocalAgent, loadAgents, loadHealthOverview, localState.info?.agent_id]);

  const claimCurrentAgent = useCallback(async () => {
    await submitClaimAgent({ forceReclaim: false });
  }, [submitClaimAgent]);

  const forceClaimCurrentAgent = useCallback(() => {
    const agentId = String(claimAgentId || localState.info?.agent_id || '').trim();
    const code = String(claimCode || '').trim();
    if (!agentId) {
      message.warning('请先输入认领ID');
      return;
    }
    if (!code) {
      message.warning('请先输入认领码');
      return;
    }
    Modal.confirm({
      title: '确认强制认领该设备？',
      content: `agent_id=${agentId}。强制认领会尝试将该设备从原账号转移到当前账号。`,
      okText: '确认强制认领',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => submitClaimAgent({ forceReclaim: true }),
    });
  }, [claimAgentId, claimCode, localState.info?.agent_id, submitClaimAgent]);

  const cleanupOfflineHistoryAgents = useCallback(() => {
    Modal.confirm({
      title: '清理离线历史 Agent',
      content: `将清理超过 ${Math.round(AGENT_HISTORY_CLEANUP_WINDOW_SEC / 3600)} 小时无心跳且未绑定账号的离线 Agent 记录。在线或近期活跃设备不会删除。`,
      okText: '开始清理',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setCleanupLoading(true);
        try {
          const response = await cleanupOfflineAgents({
            online_window_sec: 120,
            inactive_window_sec: AGENT_HISTORY_CLEANUP_WINDOW_SEC,
            include_bound_accounts: false,
            max_delete: 200,
          });
          if (response?.status !== 'success') {
            throw new Error(resolveErrorMessage(response?.error_message, '清理离线历史 Agent 失败'));
          }
          const deleted = Number(response?.deleted_count || 0);
          const skipped = Number(response?.skipped_bound_count || 0);
          const failed = Number(response?.failed_count || 0);
          const suffix = [
            skipped > 0 ? `跳过 ${skipped} 条（仍绑定账号）` : '',
            failed > 0 ? `失败 ${failed} 条` : '',
          ].filter(Boolean).join('，');
          message.success(`已清理 ${deleted} 条离线历史 Agent${suffix ? `，${suffix}` : ''}`);
          await Promise.all([
            loadAgents({ silent: true }),
            loadHealthOverview({ silent: true }),
            detectLocalAgent({ silent: true }),
          ]);
        } catch (error) {
          message.error(resolveErrorMessage(error?.message, '清理离线历史 Agent 失败'));
        } finally {
          setCleanupLoading(false);
        }
      },
    });
  }, [detectLocalAgent, loadAgents, loadHealthOverview]);

  const activeAgents = useMemo(() => {
    const list = Array.isArray(agents) ? agents : [];
    if (showHistoryOfflineAgents) return list;
    return list.filter((item) => isRecentActiveAgent(item, AGENT_RECENT_ACTIVE_WINDOW_SEC));
  }, [agents, showHistoryOfflineAgents]);

  const hiddenHistoryCount = useMemo(() => {
    if (showHistoryOfflineAgents) return 0;
    const list = Array.isArray(agents) ? agents : [];
    return list.filter((item) => !isRecentActiveAgent(item, AGENT_RECENT_ACTIVE_WINDOW_SEC)).length;
  }, [agents, showHistoryOfflineAgents]);

  const filteredAgents = useMemo(
    () => (onlineOnlyAgents ? activeAgents.filter((item) => isOnlineAgent(item)) : activeAgents),
    [activeAgents, onlineOnlyAgents],
  );

  const agentStats = useMemo(() => {
    const list = Array.isArray(filteredAgents) ? filteredAgents : [];
    const total = list.length;
    const online = list.filter((item) => isOnlineAgent(item)).length;
    const offline = Math.max(0, total - online);
    return {
      total,
      online,
      offline,
      onlineRate: total > 0 ? Math.round((online / total) * 100) : 0,
    };
  }, [filteredAgents]);

  const toPercent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

  const cloudColumns = [
    {
      title: 'Agent',
      dataIndex: 'agent_id',
      key: 'agent_id',
      width: 280,
      render: (value, row) => {
        const host = String(row?.hostname || '').trim();
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{host || value || '-'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{value || '-'}</Text>
          </Space>
        );
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 110,
      render: (_, row) => (
        <Tag color={isOnlineAgent(row) ? 'green' : 'default'}>
          {isOnlineAgent(row) ? '在线' : '离线'}
        </Tag>
      ),
    },
    {
      title: '系统',
      key: 'os',
      width: 140,
      render: (_, row) => {
        const os = String(row?.os || '-').trim();
        const arch = String(row?.arch || '-').trim();
        return `${os}/${arch}`;
      },
    },
    {
      title: '版本',
      dataIndex: 'agent_version',
      key: 'agent_version',
      width: 100,
      render: (value) => value || '-',
    },
    {
      title: '用户',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: '最近心跳',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 220,
      render: (value, row) => formatDateTime(value || row?.last_heartbeat_at),
    },
    {
      title: '绑定账号',
      dataIndex: 'bound_account_count',
      key: 'bound_account_count',
      width: 110,
      render: (value) => Number(value || 0),
    },
  ];

  const healthAgent = healthOverview?.agent_health || {};
  const healthAccount = healthOverview?.account_publish_health || {};
  const healthTask = healthOverview?.task_health || {};
  const healthPublish = healthOverview?.publish_health || {};
  const failureTopN = healthPublish.failure_top_n || [];
  const latestHeartbeat = (healthAgent.recent_heartbeats || [])[0] || null;

  const installTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="下载安装说明"
        description="下载并启动对应系统 Agent；先在本页完成认领，再在“Agent列表”确认在线，最后去“账号管理”完成账号绑定与会话检查。"
      />
      {AUTO_REGISTER_ENABLED && pairFlow.state === 'failed' ? (
        <Alert
          type="warning"
          showIcon
          message="本机 Agent 自动注册失败"
          description={pairFlow.lastError || '请确认本机 Agent 已启动，然后点击刷新重试。若当前账号与历史绑定账号不同，请先清理本机旧身份再重试。'}
        />
      ) : null}
      <Card size="small">
        <Space wrap>
          <Button type="primary" icon={<RightOutlined />} onClick={() => navigate('/users')}>
            去账号管理（绑定/会话）
          </Button>
        </Space>
      </Card>
      <Card title="认领本机 Agent" size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text type="secondary">
            在本机托盘菜单查看“认领ID/认领码”，填入后可将该设备归属到当前登录账号。
          </Text>
          <Text type="secondary">
            若提示“该设备已被其他账号认领”，可使用“强制认领”（需二次确认，且后端需开启转移策略）。
          </Text>
          <Space wrap>
            <Input
              allowClear
              placeholder="认领ID（agent_xxx）"
              value={claimAgentId}
              onChange={(e) => setClaimAgentId(e.target.value)}
              style={{ width: 320 }}
            />
            <Input
              allowClear
              placeholder="认领码（6位）"
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value)}
              onPressEnter={() => claimCurrentAgent()}
              style={{ width: 180 }}
            />
            <Button type="primary" loading={claimingAgent} onClick={() => claimCurrentAgent()}>
              认领
            </Button>
            <Button danger loading={claimingAgent} onClick={() => forceClaimCurrentAgent()}>
              强制认领
            </Button>
          </Space>
        </Space>
      </Card>
      <Row gutter={[12, 12]}>
        {downloadItems.map((item) => {
          const ready = Boolean(item.url);
          return (
            <Col key={item.key} xs={24} md={12}>
              <Card title={<Space>{item.icon}<span>{item.title}</span></Space>} size="small">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">{item.desc}</Text>
                  {!ready ? <Tag color="orange">安装包暂未就绪</Tag> : null}
                  <Space>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      disabled={!ready}
                      onClick={() => {
                        window.open(item.url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      下载
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Card title="安装指引" size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>1. 下载并安装对应系统的 Agent，安装后启动应用。</Text>
          <Text>2. 在托盘查看“认领ID/认领码”，回到本页完成认领。</Text>
          <Text>3. 等待 10-30 秒，在“Agent列表”确认该设备显示在线。</Text>
          <Text>4. 前往“账号管理”完成账号绑定与会话检查，再执行发布。</Text>
        </Space>
      </Card>
    </Space>
  );

  const agentTableTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Agent总数" value={agentStats.total} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="在线" value={agentStats.online} valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="离线" value={agentStats.offline} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="在线率" value={agentStats.onlineRate} suffix="%" /></Card>
        </Col>
      </Row>
      <Space wrap>
        <Input
          allowClear
          placeholder="按 Agent ID / 主机名搜索"
          value={agentKeyword}
          onChange={(e) => setAgentKeyword(e.target.value)}
          onPressEnter={() => loadAgents({ silent: false })}
          style={{ width: 280 }}
        />
        <Button type="primary" onClick={() => loadAgents({ silent: false })}>查询</Button>
        <Button icon={<ReloadOutlined />} onClick={refreshAll}>刷新</Button>
        <Space size={4}>
          <Switch checked={onlineOnlyAgents} onChange={setOnlineOnlyAgents} />
          <Text>仅看在线 Agent</Text>
        </Space>
        <Space size={4}>
          <Switch checked={showHistoryOfflineAgents} onChange={setShowHistoryOfflineAgents} />
          <Text>显示历史离线</Text>
        </Space>
        {!showHistoryOfflineAgents && hiddenHistoryCount > 0 ? (
          <Tag color="default">已隐藏 {hiddenHistoryCount} 条历史离线记录</Tag>
        ) : null}
        <Button danger loading={cleanupLoading} onClick={cleanupOfflineHistoryAgents}>
          清理离线历史
        </Button>
      </Space>
      <Table
        size="small"
        rowKey={(row) => String(row?.agent_id || '')}
        loading={agentsLoading}
        dataSource={filteredAgents}
        pagination={false}
        columns={cloudColumns}
      />
      <Card title="云端健康概览（最近 7 天）" size="small" loading={healthLoading}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} xl={6}>
            <Statistic
              title="最近心跳"
              value={latestHeartbeat ? formatDateTime(latestHeartbeat.last_heartbeat_at) : '-'}
              valueStyle={{ fontSize: 14 }}
            />
          </Col>
          <Col xs={12} md={6} xl={6}>
            <Statistic
              title="账号可发布率"
              value={toPercent(healthAccount.can_publish_rate)}
            />
          </Col>
          <Col xs={12} md={6} xl={6}>
            <Statistic
              title="任务成功率"
              value={toPercent(healthTask.task_success_rate)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Space wrap>
              {(failureTopN || []).slice(0, 3).map((item) => (
                <Tag key={`${item.error_code}_${item.rank}`}>{item.error_code}:{item.failed_total}</Tag>
              ))}
              {failureTopN.length === 0 ? <Tag>暂无失败 TopN</Tag> : null}
            </Space>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  return (
    <div className="page-shell agent-setup">
      <Card className="page-card" bodyStyle={{ padding: 12, overflowY: 'auto', overflowX: 'hidden' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'install', label: '下载安装', children: installTab },
            { key: 'agents', label: 'Agent列表', children: agentTableTab },
          ]}
        />
      </Card>
    </div>
  );
};

export default AgentSetup;
